import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import FileItemUpload from './FileItemUpload';
import Text from 'components/Text';
import Box from 'components/Box';
import colorTheme from 'constants/color';
import IcAccount from 'assets/Icons/IcAccount';

export interface UploadAvatarImageProps {
  value?: string | null;
  onChange?: (val: File) => void;
  style?: React.CSSProperties;
  className?: string;
  onDelete?: () => void;
}

const UploadAvatarImage = ({ value, onChange, style, className, onDelete }: UploadAvatarImageProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = (files: FileList | null) => {
    if (!files || !files.length) return;

    const file = files[0];
    const fileRename = new File([file], file.name, {
      type: file.type,
    });

    onChange?.(fileRename);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const handleDelete = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }

    onDelete?.();
  };
  
  return (
    <Container
      onClick={() => inputRef.current?.click()}
      onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
      }}
      onDrop={handleDrop}
      style={style}
      className={className}
    >
      <HiddenInput ref={inputRef} type='file' accept='image/*' onChange={handleSelect} />
      {!value ? (
        <Placeholder isDragging={isDragging}>
          <IcAccount />
          <Text variant='CONTENT_1' color='text_3_v2' textAlign='center'>
            No image uploaded
          </Text>
        </Placeholder>
      ) : (
        <FileItemUpload fileInfo={value} onDelete={handleDelete} />
      )}
    </Container>
  );
};

export default UploadAvatarImage;

const Container = styled.div`
  width: 100%;
  border-radius: 100%;
  padding: 16px;
  background: #fff;
  box-shadow: 0 4px 8px -4px rgba(0, 0, 0, 0.15);
`;

const HiddenInput = styled.input`
  display: none !important;
`;

const Placeholder = styled(Box)<{ isDragging: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 100%;
  border-radius: 100%;
  padding: 24px;

  border: 1px dashed ${({ isDragging }) => (isDragging ? colorTheme.error_3 : colorTheme.v2_border_3)};
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${colorTheme.v2_neutral_2};

  &: hover {
    border-color: ${colorTheme.hover_info};
  }
`;
